/**
 * Leviathan Web - Tank Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Create and edit storage tanks with capacity, levels, and compliance tracking
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';

import { API_URL } from '@/config/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  category: string;
}

interface Customer {
  id: string;
  name: string;
  customerNumber: string;
}

interface CustomerLocation {
  id: string;
  name: string;
  locationCode: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  customerId: string;
}

interface FormData {
  tankNumber: string;
  name: string;
  productId: string;
  capacity: number;
  currentLevel: number;
  minLevel: number;
  maxLevel: number;
  location: string;
  tankType: string;
  material: string;
  installDate: string;
  lastInspectionDate: string;
  nextInspectionDue: string;
  notes: string;
  status: string;
  customerId: string;
  customerLocationId: string;
}

const TANK_TYPES = ['ABOVE_GROUND', 'UNDERGROUND', 'MOBILE', 'PORTABLE'];
const MATERIALS = ['STEEL', 'FIBERGLASS', 'CONCRETE', 'PLASTIC', 'ALUMINUM'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'MAINTENANCE', 'OUT_OF_SERVICE'];

export default function TankFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  // Get query params for pre-selection
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedCustomerId = urlParams.get('customerId') || '';
  const preselectedLocationId = urlParams.get('locationId') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [locations, setLocations] = useState<CustomerLocation[]>([]);

  const [formData, setFormData] = useState<FormData>({
    tankNumber: '',
    name: '',
    productId: '',
    capacity: 0,
    currentLevel: 0,
    minLevel: 0,
    maxLevel: 0,
    location: '',
    tankType: 'ABOVE_GROUND',
    material: 'STEEL',
    installDate: '',
    lastInspectionDate: '',
    nextInspectionDue: '',
    notes: '',
    status: 'ACTIVE',
    customerId: preselectedCustomerId,
    customerLocationId: preselectedLocationId,
  });

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    if (isEditMode) {
      fetchTank();
    } else if (preselectedCustomerId) {
      fetchLocationsForCustomer(preselectedCustomerId);
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/inventory?category=FUEL&isActive=true&limit=100`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers?status=ACTIVE&limit=500`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchLocationsForCustomer = async (customerId: string) => {
    try {
      const response = await fetch(
        `${API_URL}/api/customer-locations?customerId=${customerId}&isActive=true`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    setFormData((prev) => ({
      ...prev,
      customerId,
      customerLocationId: '', // Reset location when customer changes
    }));
    if (customerId) {
      fetchLocationsForCustomer(customerId);
    } else {
      setLocations([]);
    }
  };

  const fetchTank = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tanks/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const tank = data.tank || data;

        // Fetch locations if tank has a customer
        if (tank.customerId) {
          await fetchLocationsForCustomer(tank.customerId);
        }

        setFormData({
          tankNumber: tank.tankNumber || '',
          name: tank.name || '',
          productId: tank.productId || '',
          capacity: tank.capacity || 0,
          currentLevel: tank.currentLevel || 0,
          minLevel: tank.minLevel || 0,
          maxLevel: tank.maxLevel || 0,
          location: tank.location || '',
          tankType: tank.tankType || 'ABOVE_GROUND',
          material: tank.material || 'STEEL',
          installDate: tank.installDate?.split('T')[0] || '',
          lastInspectionDate: tank.lastInspectionDate?.split('T')[0] || '',
          nextInspectionDue: tank.nextInspectionDue?.split('T')[0] || '',
          notes: tank.notes || '',
          status: tank.status || 'ACTIVE',
          customerId: tank.customerId || '',
          customerLocationId: tank.customerLocationId || '',
        });
      } else {
        setError('Tank not found');
      }
    } catch (error) {
      setError('Failed to load tank');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Tank name is required');
      return;
    }

    if (formData.capacity <= 0) {
      setError('Capacity must be greater than 0');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/tanks/${id}` : `${API_URL}/api/tanks`;

      const payload = {
        ...formData,
        productId: formData.productId || null,
        customerId: formData.customerId || null,
        customerLocationId: formData.customerLocationId || null,
        installDate: formData.installDate || null,
        lastInspectionDate: formData.lastInspectionDate || null,
        nextInspectionDue: formData.nextInspectionDue || null,
      };

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate('/tanks');
      } else {
        const errorMessage = await parseApiError(response);
        setError(errorMessage);
      }
    } catch (error) {
      setError('Failed to save tank');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const utilizationPercent =
    formData.capacity > 0 ? (formData.currentLevel / formData.capacity) * 100 : 0;

  const getLevelColor = () => {
    if (utilizationPercent < 25) return 'bg-error-red';
    if (utilizationPercent < 50) return 'bg-warning-amber';
    return 'bg-success-green';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading tank...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Tank' : 'New Tank'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify tank configuration' : 'Add a new storage tank'}
              </p>
            </div>
            <button
              onClick={() => navigate('/tanks')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Tanks
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Panel title="Tank Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Tank Number {!isEditMode && '(auto if blank)'}
              </label>
              <input
                type="text"
                value={formData.tankNumber}
                onChange={(e) => updateField('tankNumber', e.target.value)}
                placeholder="TNK-001"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Tank Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Main Diesel Tank"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Product
              </label>
              <select
                value={formData.productId}
                onChange={(e) => updateField('productId', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="">Select Product...</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Physical Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="Yard A, North Side"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Tank Type
              </label>
              <select
                value={formData.tankType}
                onChange={(e) => updateField('tankType', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {TANK_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Material
              </label>
              <select
                value={formData.material}
                onChange={(e) => updateField('material', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {MATERIALS.map((mat) => (
                  <option key={mat} value={mat}>
                    {mat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Customer Assignment */}
        <Panel title="Customer Assignment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Customer
              </label>
              <select
                value={formData.customerId}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                <option value="">Not Assigned</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.customerNumber})
                  </option>
                ))}
              </select>
              <p className="text-text-tertiary text-xs font-mono mt-1">
                Assign this tank to a customer for tracking deliveries
              </p>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Ship-To Location
              </label>
              <select
                value={formData.customerLocationId}
                onChange={(e) => updateField('customerLocationId', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                disabled={!formData.customerId}
              >
                <option value="">Select Location...</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                    {location.locationCode && ` (${location.locationCode})`}
                    {location.city && ` - ${location.city}`}
                    {location.state && `, ${location.state}`}
                  </option>
                ))}
              </select>
              {!formData.customerId && (
                <p className="text-text-tertiary text-xs font-mono mt-1">
                  Select a customer first to see their locations
                </p>
              )}
              {formData.customerId && locations.length === 0 && (
                <p className="text-amber text-xs font-mono mt-1">
                  No locations found for this customer
                </p>
              )}
            </div>
          </div>
        </Panel>

        {/* Capacity & Levels */}
        <Panel title="Capacity & Levels">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Total Capacity (gal) *
              </label>
              <input
                type="number"
                value={formData.capacity || ''}
                onChange={(e) => updateField('capacity', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Current Level (gal)
              </label>
              <input
                type="number"
                value={formData.currentLevel || ''}
                onChange={(e) => updateField('currentLevel', parseInt(e.target.value) || 0)}
                min="0"
                max={formData.capacity}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Min Level (gal)
              </label>
              <input
                type="number"
                value={formData.minLevel || ''}
                onChange={(e) => updateField('minLevel', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Max Level (gal)
              </label>
              <input
                type="number"
                value={formData.maxLevel || ''}
                onChange={(e) => updateField('maxLevel', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>

          {/* Visual Level Indicator */}
          {formData.capacity > 0 && (
            <div className="mt-6 p-4 bg-background border border-panel-border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-text-secondary font-mono text-sm">Tank Level</span>
                <span className="text-amber font-mono font-semibold">
                  {utilizationPercent.toFixed(1)}%
                </span>
              </div>
              <div className="h-4 bg-panel-border overflow-hidden">
                <div
                  className={`h-full ${getLevelColor()} transition-all`}
                  style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs font-mono text-text-tertiary mt-1">
                <span>0</span>
                <span>
                  {formData.currentLevel.toLocaleString()} / {formData.capacity.toLocaleString()} gal
                </span>
              </div>
            </div>
          )}
        </Panel>

        {/* Dates & Maintenance */}
        <Panel title="Maintenance & Compliance">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Install Date
              </label>
              <input
                type="date"
                value={formData.installDate}
                onChange={(e) => updateField('installDate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Last Inspection
              </label>
              <input
                type="date"
                value={formData.lastInspectionDate}
                onChange={(e) => updateField('lastInspectionDate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Next Inspection Due
              </label>
              <input
                type="date"
                value={formData.nextInspectionDue}
                onChange={(e) => updateField('nextInspectionDue', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={3}
            placeholder="Additional notes about this tank..."
            className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/tanks')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Tank' : 'Add Tank'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
